<template>
  <VPopper
    ref="popper"
    class="v-popover"
    v-bind="props"
    popper-class="v-popover-popper"
    show-arrow
    @click.stop="$emit('click', $event)"
    @destroy-popper="$emit('hide')"
    @dragend.stop="$emit('dragend', $event)"
    @dragenter.stop="$emit('dragenter', $event)"
    @dragleave.stop="$emit('dragleave', $event)"
    @dragover.stop="$emit('dragover', $event)"
    @dragstart.stop="$emit('dragstart', $event)"
    @popper-mouseenter="$emit('popperMouseenter', $event)"
    @popper-mouseleave="$emit('popperMouseleave', $event)"
  >
    <slot />

    <template #reference>
      <slot name="reference" />
    </template>
  </VPopper>
</template>

<script setup lang="ts">
import { type StyleValue, useTemplateRef } from 'vue'

import type { VPopperPlacement, VPopperTrigger } from '../types.ts'
import VPopper from './VPopper.vue'

defineEmits<{
  popperMouseenter: [MouseEvent]
  popperMouseleave: [MouseEvent]
  click: [MouseEvent]
  dragend: [DragEvent]
  dragenter: [DragEvent]
  dragleave: [DragEvent]
  dragover: [DragEvent]
  dragstart: [DragEvent]
  hide: []
}>()

interface Props {
  placement?: VPopperPlacement
  trigger?: VPopperTrigger
  tag?: string
  wrapperTag?: string
  offset?: number
  showArrow?: boolean
  lazy?: boolean
  closeOnClickOutside?: boolean
  closeOnClickReference?: boolean
  openDelay?: number
  transition?: string
  referenceStyle?: StyleValue
  referenceClass?: any
  popperClass?: any
  mountTo?: string | HTMLElement
  clickIgnoreOn?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'bottom',
  trigger: 'click',
  tag: 'div',
  wrapperTag: 'div',
  offset: 0,
  showArrow: false,
  lazy: false,
  closeOnClickOutside: true,
  closeOnClickReference: true,
  openDelay: 0,
  transition: 'v-fade',
  referenceStyle: '',
  referenceClass: '',
  popperClass: '',
  mountTo: undefined,
  clickIgnoreOn: () => [],
})

defineSlots<{
  default?(): any
  reference?(): any
}>()

const popper = useTemplateRef('popper')

function hide() {
  popper.value?.hide()
}

function show() {
  popper.value?.show()
}

defineExpose({
  hide,
  show,
  popper,
})
</script>

<style>
.v-popover-popper {
  padding: 15px 20px;
  background-color: #fff;
  border-radius: 3px;
  filter: var(--drop-shadow-light);
  cursor: default;
}
</style>
